import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, ShieldCheck, Download, CheckCircle2, Star } from 'lucide-react';
export function FinalCTA() {
  // Countdown timer logic (starting from 2 days, 14 hours, 37 mins, 12 secs)
  const [timeLeft, setTimeLeft] = useState({
    days: 2,
    hours: 14,
    minutes: 37,
    seconds: 12
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.days === 0 && prev.hours === 0 && prev.minutes === 0 && prev.seconds === 0) {
          clearInterval(timer);
          return prev;
        }
        let newSeconds = prev.seconds - 1;
        let newMinutes = prev.minutes;
        let newHours = prev.hours;
        let newDays = prev.days;
        if (newSeconds < 0) {
          newSeconds = 59;
          newMinutes -= 1;
        }
        if (newMinutes < 0) {
          newMinutes = 59;
          newHours -= 1;
        }
        if (newHours < 0) {
          newHours = 23;
          newDays -= 1;
        }
        return {
          days: newDays,
          hours: newHours,
          minutes: newMinutes,
          seconds: newSeconds
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (time: number) => time.toString().padStart(2, '0');
  return <section id="cta" className="py-24 relative overflow-hidden bg-lavender" data-id="element-73">
      {/* Decorative colored glows */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-74">
        <div className="absolute top-[-30%] left-[-15%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" data-id="element-75"></div>
        <div className="absolute bottom-[-30%] right-[-15%] w-[50%] h-[50%] rounded-full bg-accent/10 blur-[120px]" data-id="element-76"></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-77">
        {/* Header Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="flex flex-col items-center mb-16" data-id="element-78">
          <img src="https://cdn.magicpatterns.com/uploads/5SiBdLbjTF6xF5K4EEvxM1/ChatGPT_Image_23_oct_2025,_18_21_20.png" alt="Small Big Emotions Kit" className="w-full max-w-xs h-auto max-h-[240px] object-contain rounded-2xl shadow-2xl border-4 border-primary/40 mb-10" data-id="element-79" />
          <h2 className="text-3xl md:text-5xl font-heading font-extrabold text-dark mb-6 text-balance leading-tight" data-id="element-80">
            Give Your Child the Gift of Emotional Intelligence
          </h2>
          <p className="text-lg md:text-xl text-muted max-w-2xl mx-auto leading-relaxed" data-id="element-81">
            Join 2,000+ families who are raising emotionally healthy, confident
            kids — one game at a time.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16 items-stretch" data-id="element-82">
          {/* Essential Kit */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-3xl border border-dark/10 p-8 flex flex-col text-left shadow-sm hover:shadow-md transition-shadow" data-id="element-83">
            <h3 className="text-2xl font-heading font-bold text-dark mb-2" data-id="element-84">
              Essential Kit
            </h3>
            <div className="flex items-end gap-3 mb-6 pb-6 border-b border-dark/5 flex-wrap" data-id="element-85">
              <span className="text-xl line-through text-muted font-medium mb-1" data-id="element-86">
                $24.90
              </span>
              <span className="text-5xl font-heading font-black text-dark" data-id="element-87">
                $14.90
              </span>
              <span className="bg-success/15 text-success text-sm font-extrabold px-3 py-1 rounded-full mb-1 whitespace-nowrap" data-id="element-88">
                40% OFF
              </span>
            </div>

            <ul className="space-y-3 mb-8 flex-grow" data-id="element-89">
              {['Emotion Wheel', 'Emotion Traffic Light', 'Faces Game', "Card Game 'Inside Out'", 'Sensations Game', 'Expression Theatre', 'Emotions Path Board Game', 'Guess the Emotion Game', 'Calm Box', 'Exclusive Emotions UNO'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-90">
                  <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" data-id="element-91" />
                  <span className="text-dark/80" data-id="element-92">{item}</span>
                </li>)}
            </ul>

            <a href="https://mundoinfantildigital.shop/cart/43318374924390:1" target="_blank" rel="noopener noreferrer" className="w-full bg-dark hover:bg-dark/90 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:-translate-y-1 shadow-lg block text-center" data-id="element-93">
              Get Essential Kit — $14.90
            </a>
          </motion.div>

          {/* Pro Kit */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-3xl border-2 border-primary p-8 flex flex-col text-left shadow-warm-lg relative transform md:-translate-y-4" data-id="element-94">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white px-6 py-1.5 rounded-full text-sm font-extrabold tracking-wide uppercase shadow-md flex items-center gap-1.5 whitespace-nowrap" data-id="element-95">
              <Star className="w-4 h-4 fill-white" data-id="element-96" /> BEST VALUE
            </div>

            <h3 className="text-2xl font-heading font-bold text-dark mb-2 mt-2" data-id="element-97">
              Pro Kit
            </h3>
            <div className="flex items-end gap-3 mb-6 pb-6 border-b border-dark/5 flex-wrap" data-id="element-98">
              <span className="text-xl line-through text-muted font-medium mb-1" data-id="element-99">
                $49.90
              </span>
              <span className="text-6xl font-heading font-black text-primary" data-id="element-100">
                $24.90
              </span>
              <span className="bg-primary/15 text-primary text-sm font-extrabold px-3 py-1 rounded-full mb-1 whitespace-nowrap" data-id="element-101">
                50% OFF
              </span>
            </div>

            <ul className="space-y-4 mb-8 flex-grow" data-id="element-102">
              <li className="flex items-start gap-3 pb-2 border-b border-dark/5" data-id="element-103">
                <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" data-id="element-104" />
                <span className="text-dark font-bold" data-id="element-105">
                  Everything in Essential Kit (10 games)
                </span>
              </li>
              <li className="flex items-start gap-3 bg-primary/5 p-3 rounded-xl border border-primary/10" data-id="element-106">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" data-id="element-107" />
                <div data-id="element-108">
                  <div className="flex items-center gap-2 mb-1" data-id="element-109">
                    <span className="text-dark font-bold" data-id="element-110">
                      Conflict Resolution Flashcards
                    </span>
                    <span className="bg-accent text-dark text-[10px] font-extrabold px-2 py-0.5 rounded uppercase" data-id="element-111">
                      New
                    </span>
                  </div>
                  <p className="text-sm text-muted" data-id="element-112">
                    Practical tools to help kids navigate disagreements
                    peacefully.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3 bg-primary/5 p-3 rounded-xl border border-primary/10" data-id="element-113">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" data-id="element-114" />
                <div data-id="element-115">
                  <div className="flex items-center gap-2 mb-1" data-id="element-116">
                    <span className="text-dark font-bold" data-id="element-117">
                      Mindful Art Therapy Workbook
                    </span>
                    <span className="bg-accent text-dark text-[10px] font-extrabold px-2 py-0.5 rounded uppercase" data-id="element-118">
                      New
                    </span>
                  </div>
                  <p className="text-sm text-muted" data-id="element-119">
                    Creative exercises for emotional expression and calming.
                  </p>
                </div>
              </li>
            </ul>

            <a href="https://mundoinfantildigital.shop/cart/45255623311462:1" target="_blank" rel="noopener noreferrer" className="w-full bg-primary hover:bg-[#e5734e] text-white px-8 py-5 rounded-full font-bold text-xl transition-all hover:shadow-[0_0_40px_rgba(244,132,95,0.5)] hover:-translate-y-1 shadow-lg shadow-primary/30 block text-center" data-id="element-120">
              Get Pro Kit — $24.90
            </a>
          </motion.div>
        </div>

        {/* Countdown Timer */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="flex flex-col items-center mb-12" data-id="element-121">
          <div className="text-dark font-bold uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-122">
            <span className="text-xl" data-id="element-123">⏰</span> Offer ends in:
          </div>
          <div className="flex items-center gap-3 sm:gap-4" data-id="element-124">
            {[{
            label: 'Days',
            value: timeLeft.days
          }, {
            label: 'Hours',
            value: timeLeft.hours
          }, {
            label: 'Minutes',
            value: timeLeft.minutes
          }, {
            label: 'Seconds',
            value: timeLeft.seconds
          }].map((unit, i) => <div key={unit.label} className="flex flex-col items-center" data-id="element-125">
                <div className="bg-dark text-white w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-3xl sm:text-4xl font-heading font-black shadow-lg mb-2" data-id="element-126">
                  {formatTime(unit.value)}
                </div>
                <span className="text-xs sm:text-sm font-bold text-muted uppercase tracking-wide" data-id="element-127">
                  {unit.label}
                </span>
              </div>)}
          </div>
        </motion.div>

        {/* Trust Badges */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="flex flex-col sm:flex-row items-center justify-center gap-6 text-muted text-sm font-medium" data-id="element-128">
          <div className="flex items-center gap-2" data-id="element-129">
            <Lock className="w-4 h-4 text-primary" data-id="element-130" />
            <span data-id="element-131">Secure checkout</span>
          </div>
          <div className="hidden sm:block w-1 h-1 bg-dark/20 rounded-full" data-id="element-132"></div>
          <div className="flex items-center gap-2" data-id="element-133">
            <ShieldCheck className="w-4 h-4 text-primary" data-id="element-134" />
            <span data-id="element-135">30-day guarantee</span>
          </div>
          <div className="hidden sm:block w-1 h-1 bg-dark/20 rounded-full" data-id="element-136"></div>
          <div className="flex items-center gap-2" data-id="element-137">
            <Download className="w-4 h-4 text-primary" data-id="element-138" />
            <span data-id="element-139">Instant download</span>
          </div>
        </motion.div>
      </div>
    </section>;
}